import { toastContent } from "components/toastContent";
import { t } from "i18next";
import { TaskBase, TaskBaseTypes } from "pages/project/common/taskBase.typing";
import { toast } from "react-hot-toast";
import { create } from "zustand";
import { XtmQaReportResultType } from "api/api.typing";

type TaskBaseXtmQAReportState = {
  id: string;
  taskCode: string;
  toasterId: string;
  reportStatus: "INIT" | "IN_PROGRESS" | "FAILED";
};

interface TaskBaseXtmQAReportStoreState<TaskBaseType extends TaskBase> {
  reportStatuses: Map<string, TaskBaseXtmQAReportState>;
  shouldDisableAction: (id?: string) => boolean;
  requestGenerateQAReport: (id: string, taskBase: TaskBaseType, hubConnectionId: string) => void;
  onQAReportSuccess: (id: string) => void;
  onQAReportFailed: (id: string, error?: string) => void;
  onQAReportHubMessage: (taskId: number, resultType: number) => void;
}

export function createTaskBaseXtmQAReportStore<TaskBaseType extends TaskBase>(
  objectType: TaskBaseTypes,
  requestGenerateQAReport: (
    taskBase: TaskBaseType,
    hubConnectionId: string
  ) => Promise<{ reportUrl?: string; status: string; value?: string | null }>
) {
  const taskBase = (key: string, taskCode: string) => t(`${objectType}.detail.${key}`, { taskCode });

  return create<TaskBaseXtmQAReportStoreState<TaskBaseType>>((set, get) => {
    function setRequestFailed(id: string, error?: string) {
      if (error) console.error(error);
      const reportStatuses = get().reportStatuses;
      const state = reportStatuses.get(id);
      if (state?.toasterId) {
        toast.dismiss(state.toasterId);
      }
      if (!state) {
        return;
      }
      toast.error(toastContent(taskBase("title", state.taskCode), t("translation:fileDownload.failedDownload")));
      reportStatuses.delete(id);
      set({ reportStatuses });
    }

    function setRequestInProgress(id: string, taskCode: string) {
      const reportStatuses = get().reportStatuses;
      const state = reportStatuses.get(id);
      if (state?.toasterId) {
        toast.dismiss(state.toasterId);
      }

      const toasterId = toast.loading(toastContent(taskBase("title", taskCode), t("translation:common.loading")));
      reportStatuses.set(id, { id, reportStatus: "IN_PROGRESS", toasterId, taskCode });
      set({ reportStatuses });
    }

    return {
      reportStatuses: new Map<string, TaskBaseXtmQAReportState>(),
      shouldDisableAction: (id?: string) => {
        return get().reportStatuses.get(id ?? "")?.reportStatus === "IN_PROGRESS" || false;
      },
      requestGenerateQAReport: (id: string, taskBase: TaskBaseType, hubConnectionId: string) => {
        setRequestInProgress(id, taskBase.taskCode);
        requestGenerateQAReport(taskBase, hubConnectionId)
          .then(async (response) => {
            if (response.status && response.status !== "success" && response.status !== "Success") {
              setRequestFailed(id, (response.value as string) || "Unknown error");
            }
          })
          .catch((error) => {
            setRequestFailed(id, error?.message || "Failed to generate QA report");
          });
      },
      onQAReportSuccess: (id: string) => {
        const reportStatuses = get().reportStatuses;
        const state = reportStatuses.get(id);

        if (!state) {
          return;
        }

        toast.dismiss(state.toasterId);
        toast.success(toastContent(taskBase("title", state.taskCode), t("translation:fileDownload.ready")));

        reportStatuses.delete(state.id);
        set({ reportStatuses });
      },
      onQAReportFailed: (id: string, error?: string) => {
        setRequestFailed(id, error);
      },
      onQAReportHubMessage: (taskId: number, resultType: number) => {
        const reportStatuses = get().reportStatuses;

        // Convert number to string since store uses string keys
        const state = reportStatuses.get(String(taskId));

        if (!state) {
          return;
        }

        // Handle based on resultType
        if (resultType === XtmQaReportResultType.SUCCESS) {
          // Success case - dismiss loading toast and show success
          toast.dismiss(state.toasterId);
          toast.success(
            toastContent(taskBase("title", state.taskCode), t("translation:xtmQaReport.generation.completed"))
          );

          // Clean up state
          reportStatuses.delete(state.id);
          set({ reportStatuses });
        } else if (resultType === XtmQaReportResultType.ERROR) {
          // Error case - dismiss loading toast and show error
          toast.dismiss(state.toasterId);
          toast.error(toastContent(taskBase("title", state.taskCode), t("translation:xtmQaReport.generation.failed")));

          // Clean up state
          reportStatuses.delete(state.id);
          set({ reportStatuses });
        }
      },
    };
  });
}
